/**
 * TripsHeader — the trips page's opening band: a trip-aware greeting, the primary "plan a trip"
 * action, and the traveler's advisor (ELARA-750).
 *
 * Giving the page a header does two things the previous layout couldn't: it puts the primary
 * action somewhere prominent instead of tucked beside a list heading, and it states plainly that
 * a named human — Shannon — comes with the product, rather than hiding her behind an icon.
 *
 * She appears as an {@link AdvisorChip} on the same row as the primary action rather than as a
 * card off to the right: the card read as a second surface competing with the greeting, while a
 * portrait beside the button introduces her without asking for the attention a card demands.
 */
import type { ClientInstanceOf } from '@elara/client-core/firebase/types';
import type { TripModel } from '@elara/client-core/models/TripModel';

import type { GreetingTrip } from '#views/trip/trips-greeting.js';

import { useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { toDate } from '@elara/client-core/lib/date-utils';

import { useSessionState, useTripsState, useUserProfileState } from '#states/hooks.js';
import { LinkButton } from '#views/Router.js';
import { tripsGreeting } from '#views/trip/trips-greeting.js';

import { AdvisorChip } from './AdvisorChip.js';
import { ShannonIntroDialog } from './ShannonIntroDialog.js';

/** First word of the traveler's display name, for the greeting. */
function firstName(displayName: string | null | undefined): string | undefined {
  const first = displayName?.trim().split(/\s+/)[0];
  return first || undefined;
}

/**
 * What to call a trip in the headline: its destination reads better than a title the traveler may
 * never have set ("Tokyo" over "New Trip"), and an unnamed trip is no headline at all.
 *
 * The onboarding demo trip is dated like a real one, so it would otherwise win the countdown and
 * greet the traveler with a journey they never booked (ELARA-1028). `TripsState.nextTrip` excludes
 * it for the same reason.
 */
function greetingTrip(trip: ClientInstanceOf<TripModel>): GreetingTrip | undefined {
  if (trip.isDemo) return;
  const label = trip.destinations[0]?.displayName ?? (trip.title && trip.title !== 'New Trip' ? trip.title : undefined);
  if (!label) return;
  return { label, startDate: toDate(trip.startDate), endDate: toDate(trip.endDate) };
}

export function TripsHeader() {
  const sessionState = useSessionState();
  const userProfileState = useUserProfileState();
  const tripsState = useTripsState();
  const [introOpen, setIntroOpen] = useState(false);

  const name = firstName(sessionState.user?.displayName ?? userProfileState.profile?.displayName);
  const headline = tripsGreeting({
    // flatMap over map+filter: unnamed trips drop out without needing a type guard.
    trips: tripsState.recentTrips.items.flatMap((trip) => greetingTrip(trip) ?? []),
    now: new Date(),
    firstName: name,
  });

  // The band's warm accent glow rises off its bottom border and dies out before the top, so the
  // rule reads as the base of a surface rather than an arbitrary line. No background colour, so the
  // page surface still shows through.
  return (
    <div className="w-full border-b border-border bg-gradient-to-t from-accent/6 to-transparent to-45%">
      <div className="mx-auto flex w-full max-w-7xl flex-col lg:flex-row items-start lg:items-center justify-between gap-2 px-4 py-10">
        <div className="flex flex-col items-start gap-2.5">
          <h1 className="font-display text-2xl font-light">{headline}</h1>
          <p className="max-w-prose text-muted-foreground text-pretty">
            Plan with Elara whenever inspiration strikes — your travel advisor Shannon is on hand for the moments you’d
            rather talk through with a person.
          </p>
        </div>
        {/* Sized apart rather than gap-matched: the chip has no chrome of its own, so it needs more
            air than two adjacent buttons would to stop reading as part of the primary action. */}
        <div className="flex flex-wrap items-center justify-start md:justify-end gap-x-4 lg:gap-x-6 gap-y-3 mt-5 lg:mt-0">
          <LinkButton to={ROUTES.TRIP_NEW} size="lg" variant="accent" className="lg:order-2">
            Plan a new trip
          </LinkButton>
          <AdvisorChip onPress={() => setIntroOpen(true)} className="lg:order-1" />
        </div>
      </div>

      <ShannonIntroDialog open={introOpen} onOpenChange={setIntroOpen} />
    </div>
  );
}
